import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { OrganizationService } from '../../../../api/gen/api/organization.service';
import { OrganizationParkerEnrichedDto } from '../../../../api/gen/model/organizationParkerEnrichedDto';
import { OrganizationMemberRole } from '../../../../api/gen/model/organizationMemberRole';
import { JjamesButtonComponent, JJamesAlertComponent } from 'shared-lib';

export interface ChangeMemberRoleDialogData {
    organizationId: string;
    parker: OrganizationParkerEnrichedDto;
}

@Component({
    selector: 'app-change-member-role-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesAlertComponent
    ],
    template: `
    <div class="p-6 min-w-[340px]">
        <h2 class="text-lg font-semibold text-gray-900 mb-1">Change Member Role</h2>
        <p class="text-sm text-gray-500 mb-4">
            {{ parkerDisplayName }}
        </p>

        @if (errorMessage) {
        <div class="mb-4">
            <jjames-alert type="error" [dismissible]="true" (dismissed)="errorMessage = null">
                {{ errorMessage }}
            </jjames-alert>
        </div>
        }

        <form [formGroup]="form" (ngSubmit)="onSubmit()">
            <mat-form-field class="w-full">
                <mat-label>Role</mat-label>
                <mat-select formControlName="newRole">
                    <mat-option [value]="Role.Member">Member (parker app only)</mat-option>
                    <mat-option [value]="Role.Admin">Admin (can manage this organization)</mat-option>
                </mat-select>
            </mat-form-field>

            <div class="flex justify-end gap-3 mt-4">
                <jjames-button type="button" variant="secondary" (clicked)="onCancel()" [disabled]="submitting">
                    Cancel
                </jjames-button>
                <jjames-button type="submit" variant="primary" [disabled]="submitting || form.invalid || !hasChanged">
                    {{ submitting ? 'Saving...' : 'Save' }}
                </jjames-button>
            </div>
        </form>
    </div>
    `
})
export class ChangeMemberRoleDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<ChangeMemberRoleDialogComponent>);
    private readonly data = inject<ChangeMemberRoleDialogData>(MAT_DIALOG_DATA);
    private readonly orgService = inject(OrganizationService);
    private readonly snackBar = inject(MatSnackBar);
    private readonly fb = inject(FormBuilder);

    readonly Role = OrganizationMemberRole;
    submitting = false;
    errorMessage: string | null = null;

    form = this.fb.group({
        newRole: [this.data.parker.role ?? OrganizationMemberRole.Member, Validators.required]
    });

    get parkerDisplayName(): string {
        const p = this.data.parker;
        if (p.firstName || p.lastName) {
            return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() + (p.email ? ` · ${p.email}` : '');
        }
        return p.email ?? p.parkerId ?? '';
    }

    get hasChanged(): boolean {
        return this.form.value.newRole !== this.data.parker.role;
    }

    onSubmit(): void {
        if (this.form.invalid || !this.hasChanged) return;
        this.submitting = true;
        this.errorMessage = null;

        this.orgService.organizationsOrganizationIdParkersRolePut(
            this.data.organizationId,
            { parkerId: this.data.parker.parkerId!, newRole: this.form.value.newRole! }
        ).subscribe({
            next: () => {
                this.submitting = false;
                this.snackBar.open('Role updated', 'Close', { duration: 3000 });
                this.dialogRef.close(true);
            },
            error: (err: any) => {
                this.submitting = false;
                this.errorMessage = err.error?.title || 'Failed to update role.';
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
}
